import { cn } from "@/lib/utils";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * Quick-filter pills for the work-items list. Port of the Next
 * `quick-filters.tsx`. The pills behave like radio buttons — the
 * lit one is the one whose URL filter set matches *exactly*. Each
 * pill replaces the whole filter set; "All · reset" wipes it.
 *
 * Framework swaps: `next/navigation` (usePathname / useSearchParams)
 * -> TanStack `useRouterState`; navigation goes through
 * `useNavigate` with a search updater so it stays SPA.
 */

const FILTER_KEYS = [
  "status",
  "priority",
  "type",
  "assignee",
  "author",
  "watcher",
  "dueBefore",
  "dueAfter",
  "scope",
] as const;

type FilterParams = Record<string, string>;

function isActive(current: Record<string, unknown>, filterParams: FilterParams): boolean {
  // Exclusive match — the pill is "on" only when the URL's filter set
  // is EXACTLY the pill's params, no more, no less.
  const present = FILTER_KEYS.filter((k) => current[k]);
  const expected = Object.keys(filterParams);
  if (present.length !== expected.length) return false;
  return Object.entries(filterParams).every(([k, v]) => current[k] === v);
}

function getThisWeekRange(): { dueAfter: string; dueBefore: string } {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() + 1);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return {
    dueAfter: monday.toISOString().split("T")[0] ?? "",
    dueBefore: sunday.toISOString().split("T")[0] ?? "",
  };
}

type Props = {
  currentUserId: string;
  /** When true the viewer is scoped to "their" items by default
   *  (developers etc.) — controls which pill lights up on a bare URL. */
  defaultMine?: boolean;
};

export function QuickFilters({ currentUserId, defaultMine = false }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;
  const { dueAfter, dueBefore } = getThisWeekRange();

  // Every pill carries an explicit `scope` so closed items never leak
  // in and the developer-default Mine injection (bare URL only) never
  // fires for a non-Mine pill. "All · reset" wipes every scope.
  const filters: Array<{ label: string; params: FilterParams }> = [
    { label: t("workItemList.quick.open"), params: { scope: "open" } },
    { label: t("workItemList.quick.mine"), params: { scope: "open", assignee: currentUserId } },
    {
      label: t("workItemList.quick.reportedByMe"),
      params: { scope: "open", author: currentUserId },
    },
    { label: t("workItemList.quick.bugs"), params: { scope: "open", type: "bug" } },
    { label: t("workItemList.quick.hotfixes"), params: { scope: "open", type: "hotfix" } },
    { label: t("workItemList.quick.highPriority"), params: { scope: "open", priority: "high" } },
    { label: t("workItemList.quick.dueThisWeek"), params: { scope: "open", dueAfter, dueBefore } },
  ];

  /** Replace the URL filter set with exactly this pill's params.
   *  `q` (search) survives so refining a search with pills doesn't
   *  drop the query. */
  function applyPill(filterParams: FilterParams) {
    navigate({
      // @ts-expect-error generic search updater — TanStack typed routes
      // can't statically validate a filter-set replacement.
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev };
        for (const k of FILTER_KEYS) next[k] = undefined;
        next.page = undefined;
        for (const [k, v] of Object.entries(filterParams)) {
          if (v) next[k] = v;
        }
        return next;
      },
      replace: true,
    });
  }

  /** "All" wipes every filter + paging + the search query entirely. */
  function applyReset() {
    navigate({
      // @ts-expect-error generic search updater — see applyPill.
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev };
        for (const k of FILTER_KEYS) next[k] = undefined;
        next.page = undefined;
        next.q = undefined;
        return next;
      },
      replace: true,
    });
  }

  const noAnyFilter = !FILTER_KEYS.some((k) => search[k]);
  const pillCls =
    "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors border";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {filters.map((filter) => {
        // Developer default: a bare URL means the server quietly
        // injects assignee = currentUserId, so the "Mine" pill should
        // light up even though the URL carries no params.
        const developerDefaultActive =
          defaultMine && noAnyFilter && filter.params.assignee === currentUserId;
        const active = developerDefaultActive || isActive(search, filter.params);
        return (
          <button
            key={filter.label}
            type="button"
            onClick={() => applyPill(filter.params)}
            className={cn(
              pillCls,
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {filter.label}
          </button>
        );
      })}
      <button
        type="button"
        onClick={applyReset}
        title={t("workItemList.quick.resetTitle")}
        className={cn(
          pillCls,
          // "All" is a reset button, never an "active" filter state.
          "bg-background text-muted-foreground border-dashed border-border hover:bg-accent hover:text-accent-foreground",
        )}
      >
        {t("workItemList.quick.reset")}
      </button>
    </div>
  );
}
